/**
 * Web localStorage persistence — used by Vite PWA.
 */
import type { StorageAdapter, StorageInfo } from './storage.types';

export type {
  StorageAdapter,
  StorageBackend,
  StorageInfo,
} from './storage.types';

const memory = new Map<string, string>();
let backend: StorageInfo['backend'] = 'memory';
let persistent = false;

function probeLocalStorage(): Storage | null {
  try {
    if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
      return null;
    }
    const ls = globalThis.localStorage;
    const probeKey = '__planszowki_storage_probe__';
    ls.setItem(probeKey, '1');
    ls.removeItem(probeKey);
    return ls;
  } catch {
    return null;
  }
}

const local = probeLocalStorage();
if (local) {
  backend = 'local-storage';
  persistent = true;
}

export const storage: StorageAdapter = {
  async getItem(key) {
    if (local) return local.getItem(key);
    return memory.get(key) ?? null;
  },
  async setItem(key, value) {
    if (local) {
      local.setItem(key, value);
      return;
    }
    memory.set(key, value);
  },
  async removeItem(key) {
    if (local) {
      local.removeItem(key);
      return;
    }
    memory.delete(key);
  },
  async multiGet(keys) {
    return Promise.all(
      keys.map(async (key) => [key, await storage.getItem(key)] as [string, string | null])
    );
  },
  async multiRemove(keys) {
    await Promise.all(keys.map((key) => storage.removeItem(key)));
  },
};

export function getStorageInfo(): StorageInfo {
  return { backend, persistent };
}
